import GraphqlClient from './GraphqlClients';
import gql from 'graphql-tag';

// TODO: mover esta orquestación al backend, en una o más queries, para que la traza
// llegue resuelta de una sola vez. Ahí sí tiene sentido un hook de Apollo con caché.
const DocumentTraceService = () => {
  const gqlClient = GraphqlClient();

  const scraperDocumentQuery = gql`
    query ScraperDocument($id: String!) {
      document: scp_document(id: $id) {
        id
        createdAt
        updatedAt
        accountId
        documentStorageRef
        documentHash
        requiresTransformation
        type
        status
        date
        cardBrand
        relatedDocumentId
        authorizedDocumentTypeId
        processStatus
        link
        parsed
        processInApi
      }
    }
  `;

  const parserDocumentsQuery = gql`
    query ParserDocuments($filter: parser_DocumentsFilter) {
      documents: parser_documents(filter: $filter) {
        id
        createdAt
        updatedAt
        scrapedDocumentId
        scrapedDocumentStorageRef
        parsedDocumentStorageRef
        country
        documentType
        infoProvider
        credentialId
        majorVersion
        minorVersion
        patchVersion
        split
        lineCount
        origin
      }
    }
  `;

  const parserFailsQuery = gql`
    query ParserFails($filter: parser_ParserFailsFilter) {
      fails: parser_parserFails(filter: $filter) {
        id
        createdAt
        updatedAt
        scrapedDocumentId
        line
        debugMessage
        exceptionClass
        backtrace
        message
        link
      }
    }
  `;

  const paymentQuery = gql`
    query Payment($id: String!) {
      payment: api_payment(id: $id) {
        id
        createdAt
        paymentDate
        presentationDate
        infoProvider
        documentType
        cardBrandId
        clearingNumber
        scrapedDocumentExtId
        parsedDocumentExtId
        totalAmount
        totalEarn
        focusedForApi
      }
    }
  `;

  const paymentsQuery = gql`
    query Payments($filter: api_PaymentsFilter, $limit: Int) {
      payments: api_payments(filter: $filter, limit: $limit) {
        id
        createdAt
        paymentDate
        presentationDate
        infoProvider
        documentType
        cardBrandId
        clearingNumber
        scrapedDocumentExtId
        parsedDocumentExtId
        totalAmount
        totalEarn
        focusedForApi
      }
    }
  `;

  const paymentFailsQuery = gql`
    query PaymentFails($filter: api_PaymentFailsFilter, $limit: Int) {
      fails: api_paymentFails(filter: $filter, limit: $limit) {
        id
        createdAt
        scrapedDocumentExtId
        parsedDocumentExtId
        establishmentNumber
        clearingNumber
        debugMessage
        moreInfo
        message
        line
      }
    }
  `;

  const query = (options) =>
    gqlClient.query({ fetchPolicy: 'network-only', ...options }).then((data) => data.data);

  const getScraperDocument = (id) =>
    query({ query: scraperDocumentQuery, variables: { id } }).then((data) => data?.document);

  const getParserDocuments = (filter) =>
    query({ query: parserDocumentsQuery, variables: { filter } }).then(
      (data) => data.documents || []
    );

  const getParserFails = (scrapedDocumentIds) =>
    query({ query: parserFailsQuery, variables: { filter: { scrapedDocumentIds } } }).then(
      (data) => data.fails || []
    );

  const getPayment = (id) =>
    query({ query: paymentQuery, variables: { id } }).then((data) => data?.payment);

  const getPayments = (filter) =>
    query({ query: paymentsQuery, variables: { filter, limit: 200 } }).then(
      (data) => data.payments || []
    );

  const getPaymentFails = (filter) =>
    query({ query: paymentFailsQuery, variables: { filter, limit: 200 } }).then(
      (data) => data.fails || []
    );

  const compact = (collection) => collection.filter(Boolean);
  const uniqueById = (collection) => {
    const byId = {};
    collection.forEach((item) => {
      byId[item.id] = item;
    });
    return Object.values(byId);
  };

  const trace = async (rawInput) => {
    const input = rawInput.trim();
    const [scraperDocument, parserDocumentsById, paymentById] = await Promise.all([
      getScraperDocument(input),
      getParserDocuments({ ids: [input] }),
      getPayment(input)
    ]);

    const parserDocumentById = parserDocumentsById[0];
    const scrapedDocumentId =
      scraperDocument?.id ||
      parserDocumentById?.scrapedDocumentId ||
      paymentById?.scrapedDocumentExtId;

    if (!scrapedDocumentId) {
      return {
        input,
        scraperDocuments: [],
        parserDocuments: parserDocumentsById,
        parserFails: [],
        payments: compact([paymentById]),
        paymentFails: [],
        found: Boolean(parserDocumentById || paymentById)
      };
    }

    const rootScraperDocument = scraperDocument || (await getScraperDocument(scrapedDocumentId));
    const relatedScraperDocument = rootScraperDocument?.relatedDocumentId
      ? await getScraperDocument(rootScraperDocument.relatedDocumentId)
      : null;
    const scraperDocuments = uniqueById(compact([rootScraperDocument, relatedScraperDocument]));
    const scrapedDocumentIds = uniqueById(
      scraperDocuments.map((document) => ({ id: document.id }))
    ).map((document) => document.id);

    const [parserDocuments, parserFails, paymentsByDocument, paymentFailsByDocument] =
      await Promise.all([
        getParserDocuments({ scrapedDocumentIds }),
        getParserFails(scrapedDocumentIds),
        Promise.all(scrapedDocumentIds.map((id) => getPayments({ scrapedDocumentExtId: id }))),
        Promise.all(scrapedDocumentIds.map((id) => getPaymentFails({ scrapedDocumentExtId: id })))
      ]);
    const payments = paymentsByDocument.reduce((memo, collection) => [...memo, ...collection], []);
    const paymentFails = paymentFailsByDocument.reduce(
      (memo, collection) => [...memo, ...collection],
      []
    );

    return {
      input,
      scrapedDocumentId,
      scraperDocuments,
      parserDocuments,
      parserFails,
      payments: uniqueById(compact([paymentById, ...payments])),
      paymentFails,
      found: true
    };
  };

  return { trace };
};

export default DocumentTraceService;
